<script setup>
import { ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useLabStore } from '@/stores/lab';
import { storeToRefs } from 'pinia';
import { trans } from 'laravel-vue-i18n';

const toast = useToast();
const labStore = useLabStore();
const { active: lab } = storeToRefs(labStore);
const { updatePricing } = labStore;

const pricingSchemes = ref([]);

const newPricingScheme = ref(null);

watch(lab, (newLab) => {
    if (newLab.pricing_schemes) {
        pricingSchemes.value = [...newLab.pricing_schemes];
    }

    if (newLab) {
        lab.value.embedded_markup = Number(lab.value.embedded_markup * 100).toFixed(2); // Convert to percentage
        lab.value.rx_markup = Number(lab.value.rx_markup * 100).toFixed(2); // Convert to percentage
    }
});

const addThreshold = (schemeIndex) => {
    pricingSchemes.value[schemeIndex].lab_scheme_prices.push({
        unit_threshold: 0,
        minutes: 0,
    });
};

const addLinearThreshold = (schemeIndex) => {
    pricingSchemes.value[schemeIndex].linear_pricing_rules.push({
        unit_threshold: 0
    });
};

const removeThreshold = (schemeIndex, thresholdIndex) => {
    pricingSchemes.value[schemeIndex].lab_scheme_prices.splice(thresholdIndex, 1);
    // if the scheme has no thresholds left, remove the scheme
    if (checkIfRemoveScheme(schemeIndex)) {
        pricingSchemes.value.splice(schemeIndex, 1);
    }
};

const removeLinearThreshold = (schemeIndex, linearThresholdIndex) => {
    pricingSchemes.value[schemeIndex].linear_pricing_rules.splice(linearThresholdIndex, 1);
    // if the scheme has no linear thresholds left, remove the scheme
    if (checkIfRemoveScheme(schemeIndex)) {
        pricingSchemes.value.splice(schemeIndex, 1);
    }
};

const checkIfRemoveScheme = (schemeIndex) => {
    if (pricingSchemes.value[schemeIndex].lab_scheme_prices.length === 0 &&
        pricingSchemes.value[schemeIndex].linear_pricing_rules.length === 0) {
        pricingSchemes.value.splice(schemeIndex, 1);
    }
};

const save = async () => {
    const payload = {
        hash: lab.value.hash,
        embedded_markup: lab.value.embedded_markup / 100,
        rx_markup: lab.value.rx_markup / 100,
        dispensing_fee: lab.value.dispensing_fee,
        minute_rate: lab.value.minute_rate,
        pricingSchemes: pricingSchemes.value,
        
    };

    try {
        await updatePricing(payload);
        toast.success(trans('label.pricing_updated_successfully'));
    } catch (e) {
        toast.error(e.response.data.message);
    }
    
};

// Add a new pricing scheme to schemePrices
const addPricingScheme = () => {
    pricingSchemes.value.push({
        scheme_name: newPricingScheme.value,
        lab_scheme_prices: [
            { 'unit_threshold': 999, 'minutes': 60 }
        ],
        linear_pricing_rules: [
            { 'unit_threshold': 100 }
        ]
    });

    newPricingScheme.value = null;
};
</script>

<template>
<MainContentLayout>
    <template #breadcrumb>
        <BreadCrumb />
    </template>
    <template #title>{{ $t("label.pricing_management") }}</template>            
    <div class="grid grid-cols-1 gap-y-4 md:grid-cols-2 md:gap-x-4">
        <div class="col-span-full text-xl font-bold">
            {{ $t("label.prices") }}
        </div>
        <div class="col-span-full p-4 bg-grey-200 rounded-xl shadow">
            <div
                class="grid grid-cols-4 gap-x-4 gap-y-2 items-center font-bold text-sm uppercase py-4 mb-4 bg-grey-300 px-4">
                <div>{{ $t("label.unit_threshold") }}</div>
                <div>{{ $t("label.minutes") }}</div>
                <div>{{ $t("label.actions") }}</div>
            </div>
            <div v-for="(scheme, schemeIndex) in pricingSchemes" :key="schemeIndex">
                <div class="py-2 my-2 grid grid-cols-3">
                    <StyledInput
                        v-model="scheme.scheme_name"
                        :name="`scheme_name_${schemeIndex}`"
                        class="text-lg font-bold col-span-1"
                        :label="$t('label.scheme_name')" />
                </div>
                <div
                    v-for="(threshold, thresholdIndex) in scheme.lab_scheme_prices"
                    :key="thresholdIndex"
                    class="grid grid-cols-3 gap-x-4 gap-y-2 items-center">
                    <StyledMask
                        v-model="threshold.unit_threshold"
                        :name="`unit_thresh_${thresholdIndex}_${scheme.scheme_name}`"
                        :options="{ mask: '#######' }"
                        :placeholder="$t('label.unit_threshold')"
                        :label="$t('label.unit_threshold')" />
                    <StyledInput
                        v-model="threshold.minutes"
                        :name="`minutes_${thresholdIndex}_${scheme.scheme_name}`"
                        type="number"
                        :placeholder="$t('label.minutes')"
                        :label="$t('label.minutes')" />
                    <div class="text-red-500">
                        <div class="flex gap-2 cursor-pointer hover:text-red-600 hover:font-bold" @click="removeThreshold(schemeIndex, thresholdIndex)">
                            <v-icon
                                class="self-center mb-4"
                                color="red">mdi-delete-outline</v-icon>
                            <div class="self-center mb-3">{{ $t("label.remove") }}</div>
                        </div>
                    </div>
                </div>
                <div 
                    class="w-2/3 flex items-start justify-end mb-6"
                    style="height: auto; min-height: 50px;">
                    <StyledButton
                        variant="outlined"
                        class="flex items-center gap-2"
                        :label="$t('label.add_threshold')"
                        @click="addThreshold(schemeIndex)">
                        <FontAwesomeIcon icon="plus" class="mr-1" />
                        {{ $t('label.add_threshold') }}
                    </StyledButton>
                </div>

                <!-- Linear Pricing Rules -->
                <div v-if="scheme.linear_pricing_rules.length != 0" class="border-t-2 border-grey-300"> 
                    <div
                        v-for="(linearThreshold, linearThresholdIndex) in scheme.linear_pricing_rules"
                        :key="linearThresholdIndex"
                        :class="linearThresholdIndex === 0 ? 'pt-6' : ''"
                        class="grid grid-cols-3 gap-x-4 gap-y-2 items-center">
                        <StyledMask
                            v-model="linearThreshold.unit_threshold"
                            :name="`linear_unit_thresh_${linearThresholdIndex}_${scheme.scheme_name}`"
                            :options="{ mask: '#######' }"
                            :placeholder="$t('label.unit_threshold')"
                            :label="$t('label.unit_threshold')" />
                        <div class="text-red-500">
                            <div class="flex gap-2 cursor-pointer hover:text-red-600 hover:font-bold" @click="removeLinearThreshold(schemeIndex, linearThresholdIndex)">
                                <v-icon
                                    class="self-center mb-4"
                                    color="red">mdi-delete-outline</v-icon>
                                <div class="self-center mb-3">{{ $t("label.remove") }}</div>
                            </div>
                        </div>
                    </div>
                </div>
               
                <div 
                    class="w-2/3 flex items-start justify-end mb-4 h-auto min-h-[50px]">
                    <StyledButton
                        variant="outlined"
                        class="flex items-center gap-2"
                        :label="$t('label.add_threshold')"
                        @click="addLinearThreshold(schemeIndex)">
                        <FontAwesomeIcon icon="plus" class="mr-1" />
                        {{ $t('label.add_linear_pricing_rule') }}
                    </StyledButton>
                </div>
            </div>
            <div class="grid grid-cols-4 gap-4">
                <div class="col-span-full text-lg font-bold">
                    {{ $t("label.new_pricing_scheme") }}
                </div>
                <StyledInput
                    v-model="newPricingScheme"
                    :name="`new_pricing_scheme`" />
                <div>
                    <StyledButton
                        name="add_pricing_scheme"
                        class="self-center mt-3"
                        @click="addPricingScheme">
                        {{ $t("label.add_pricing_scheme") }}
                    </StyledButton>
                </div>
            </div>
        </div>

        <!-- Markups and Fees -->
        <div class="col-span-full text-xl font-bold">
            {{ $t("label.markups") }}
        </div>
        <StyledInput
            v-model="lab.embedded_markup"
            type="numeric"
            name="embedded_markup"
            :label="$t('label.embedded_markup_percentage')" />
        <StyledInput
            v-model="lab.rx_markup"
            type="numeric"
            name="rx_markup"
            :label="$t('label.rx_markup_percentage')" />
        <div class="col-span-full text-xl font-bold">
            {{ $t("label.fees") }}
        </div>
        <StyledCurrency
            v-model="lab.dispensing_fee"
            :placeholder="$t('label.dispensing_fee')"
            name="dispensing_fee"
            :label="$t('label.dispensing_fee')" />
        <StyledCurrency
            v-model="lab.minute_rate"
            :placeholder="$t('label.minute_rate')"
            name="minute_rate"
            :label="$t('label.minute_rate')" />
        <div>
            <StyledButton @click="save">
                <v-icon color="white" class="mr-2">mdi-content-save</v-icon>
                {{ $t("label.save") }}
            </StyledButton>
        </div>
    </div>
</MainContentLayout>
</template>
